import {
	FeaturedIcon,
	IconMessageChatbot,
	IconSend01,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import { ChatInput } from "@exopengithub/exokit/chat";
import { IconSend } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AI_PRODUCT_NAME } from "src/common/branding";
import { ConversationListItem } from "src/features/ai-chat/components/ConversationListItem";
import { DashboardWidgetBase } from "./DashboardWidgetBase.js";

export type AiChatWidgetState =
	| "loading"
	| "noAccess"
	| "notOptedIn"
	| "needsOnboarding"
	| "ready";

interface AiChatConversationItem {
	id: string;
	title: string;
}

interface AiChatWidgetViewProps {
	state: AiChatWidgetState;
	suggestions: string[];
	recentConversations: AiChatConversationItem[];
	recentConversationsError?: boolean;
	onSubmitPrompt: (prompt: string) => void;
	onOpenConversation: (conversationId: string) => void;
	/** Hands off to the full /ai-chat page — for company opt-in or, if that's already done, this user's own onboarding consent. */
	onOptIn: () => void;
	noAccessReason?: "no-grant" | "needs-domain-wide-access";
	action?: React.ReactNode;
}

export const AiChatWidgetView = ({
	state,
	suggestions,
	recentConversations,
	recentConversationsError = false,
	onSubmitPrompt,
	onOpenConversation,
	onOptIn,
	noAccessReason,
	action,
}: AiChatWidgetViewProps) => {
	const { t } = useTranslation();
	const [prompt, setPrompt] = useState("");

	return (
		<DashboardWidgetBase title={AI_PRODUCT_NAME} action={action} flush>
			{state === "loading" ? (
				<p className="p-6 text-sm text-grey-500">{t("Loading")}</p>
			) : state === "ready" ? (
				<div className="flex flex-col gap-4 pt-6">
					<div className="px-6">
						<ChatInput
							value={prompt}
							onChange={setPrompt}
							onSend={(message) => {
								onSubmitPrompt(message);
								setPrompt("");
							}}
							placeholderLabel={t("Ask a question")}
							sendLabel={t("Send message")}
						/>
					</div>
					<div className="flex flex-wrap gap-2 px-6">
						{suggestions.map((suggestion) => (
							<button
								key={suggestion}
								type="button"
								onClick={() => onSubmitPrompt(suggestion)}
								className="flex items-center gap-1.5 rounded-full border border-purple-100 bg-purple-25 px-3 py-1 text-xs font-medium text-purple-700 hover:bg-purple-50"
							>
								<IconMessageChatbot size={14} />
								{suggestion}
							</button>
						))}
					</div>
					<div>
						<h3 className="mb-2 flex items-center gap-1.5 px-6 text-sm font-semibold text-grey-900">
							<IconMessageChatbot size={16} className="text-grey-300" />
							{t("Latest chats")}
						</h3>
						{recentConversationsError ? (
							<p className="px-6 pb-6 text-sm text-grey-500">
								{t("Could not load conversations.")}
							</p>
						) : recentConversations.length === 0 ? (
							<p className="px-6 pb-6 text-sm text-grey-500">
								{t("No conversations yet.")}
							</p>
						) : (
							<ul>
								{recentConversations.map((conversation) => (
									<li key={conversation.id}>
										<ConversationListItem
											title={conversation.title}
											onClick={() => onOpenConversation(conversation.id)}
											ariaLabel={`${t("Continue")}: ${conversation.title}`}
											trailing={
												<IconSend01
													size={14}
													className="shrink-0 text-grey-300"
												/>
											}
										/>
									</li>
								))}
							</ul>
						)}
					</div>
				</div>
			) : state === "notOptedIn" ? (
				<div className="flex flex-col items-center gap-4 px-6 py-10 text-center">
					<FeaturedIcon size="lg" theme="light_circle" variant="primary">
						<IconMessageChatbot />
					</FeaturedIcon>
					<div>
						<h3 className="text-lg font-semibold text-grey-900">
							{t("Activate {{aiProductName}}")}
						</h3>
						<p className="text-sm text-gray-600">
							{t(
								"Opt in to {{aiProductName}} to start asking questions about your data.",
							)}
						</p>
					</div>
					<Button
						onClick={onOptIn}
						icon={<IconSend size={16} />}
						iconPosition="trailing"
					>
						{t("Get started")}
					</Button>
				</div>
			) : state === "needsOnboarding" ? (
				<div className="flex flex-col items-center gap-4 px-6 py-10 text-center">
					<FeaturedIcon size="lg" theme="light_circle" variant="primary">
						<IconMessageChatbot />
					</FeaturedIcon>
					<div>
						<h3 className="text-lg font-semibold text-grey-900">
							{t("Approve the terms to start chatting")}
						</h3>
						<p className="text-sm text-gray-600">
							{t(
								"You need to approve a few terms before you can use {{aiProductName}}.",
							)}
						</p>
					</div>
					<Button
						onClick={onOptIn}
						icon={<IconSend size={16} />}
						iconPosition="trailing"
					>
						{t("Get started")}
					</Button>
				</div>
			) : (
				<p className="p-6 text-sm text-gray-600">
					{noAccessReason === "needs-domain-wide-access"
						? t(
								"{{aiProductName}} answers for every company in the domain, so it requires access to all of them.",
							)
						: t("{{aiProductName}} is not available for this account yet.")}
				</p>
			)}
		</DashboardWidgetBase>
	);
};
